import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ChevronLeft, ChevronRight, Eye, Package, CheckCircle, Clock, Truck, RotateCcw, XCircle } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';

const PAGE_SIZE = 20;

const TodaysOrders = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [emirateWise, setEmirateWise] = useState([]);
  const [areaWise, setAreaWise] = useState([]);
  const [statusWise, setStatusWise] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);

  const loadList = async (pageNum = 1) => {
    try {
      setListLoading(true);
      const skip = (pageNum - 1) * PAGE_SIZE;
      const response = await MainService.TodaysDeliveryList(skip, PAGE_SIZE);
      const data = response?.Data ?? response?.data ?? [];
      const totalCount = response?.Total ?? response?.total ?? 0;
      setList(Array.isArray(data) ? data : []);
      setTotal(typeof totalCount === 'number' ? totalCount : 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Error loading today\'s orders:', err);
      setList([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  };

  const loadStatsAndCharts = async () => {
    try {
      const [statsRes, emirateRes, areaRes, statusRes] = await Promise.all([
        MainService.TodaysDeliveryStatistics(),
        MainService.EmirateWiseDelivery(),
        MainService.AreaWiseOrders(),
        MainService.StatusWiseOrders(),
      ]);
      setStats(statsRes);
      setEmirateWise(Array.isArray(emirateRes) ? emirateRes : []);
      setAreaWise(Array.isArray(areaRes) ? areaRes : []);
      setStatusWise(Array.isArray(statusRes) ? statusRes : []);
    } catch (err) {
      console.error('Error loading today\'s stats:', err);
      setStats(null);
      setEmirateWise([]);
      setAreaWise([]);
      setStatusWise([]);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadList(1), loadStatsAndCharts()]);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (page > 1 && !loading) loadList(page);
  }, [page]);

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([loadList(page), loadStatsAndCharts()]).finally(() => setLoading(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const formatDate = (d) => {
    if (d == null || d === '') return '-';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (date instanceof Date && !isNaN(date.getTime())) {
      return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    return '-';
  };

  const statCards = useMemo(
    () => [
      { key: 'TotalOrders', labelPath: 'orders.todaysOrders.statTotalOrders', icon: Package, color: 'bg-blue-50 text-blue-700 border-blue-200' },
      { key: 'Delivered', labelPath: 'orders.todaysOrders.statDelivered', icon: CheckCircle, color: 'bg-green-50 text-green-700 border-green-200' },
      { key: 'Pending', labelPath: 'orders.todaysOrders.statPending', icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
      { key: 'TRANSIT', labelPath: 'orders.todaysOrders.statInTransit', icon: Truck, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
      { key: 'RETURN', labelPath: 'orders.todaysOrders.statReturn', icon: RotateCcw, color: 'bg-orange-50 text-orange-700 border-orange-200' },
      { key: 'Cancelled', labelPath: 'orders.todaysOrders.statCancelled', icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200' },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'todaysOrders')}</h1>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-70"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {ui('common.refresh')}
        </button>
      </div>

      {loading && !stats ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : (
        <>
          {/* Statistics */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {statCards.map(({ key, labelPath, icon: Icon, color }) => (
                <div key={key} className={`rounded-xl border p-4 ${color}`}>
                  <Icon className="w-5 h-5 mb-2 opacity-80" />
                  <p className="text-2xl font-bold">{stats[key] ?? 0}</p>
                  <p className="text-sm opacity-90">{ui(labelPath)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Emirate / Area / Status breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{ui('orders.todaysOrders.byEmirate')}</h2>
              {emirateWise.length === 0 ? (
                <p className="text-sm text-gray-500">{ui('orders.todaysOrders.noData')}</p>
              ) : (
                <ul className="space-y-2">
                  {emirateWise.map((row, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700">{row.Emirate || '-'}</span>
                      <span className="font-medium text-gray-900">{row.TotalOrders ?? 0}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{ui('orders.todaysOrders.byArea')}</h2>
              {areaWise.length === 0 ? (
                <p className="text-sm text-gray-500">{ui('orders.todaysOrders.noData')}</p>
              ) : (
                <ul className="space-y-2">
                  {areaWise.map((row, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700">{row.Area || '-'}</span>
                      <span className="font-medium text-gray-900">{row.TotalOrders ?? 0}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{ui('orders.todaysOrders.byStatus')}</h2>
              {statusWise.length === 0 ? (
                <p className="text-sm text-gray-500">{ui('orders.todaysOrders.noData')}</p>
              ) : (
                <ul className="space-y-2">
                  {statusWise.map((row, i) => (
                    <li key={i} className="flex justify-between items-center text-sm gap-2">
                      <span
                        className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: row.Color ? `${row.Color}20` : '#e5e7eb',
                          color: row.Color || '#374151',
                        }}
                      >
                        {row.Status || '-'}
                      </span>
                      <span className="font-medium text-gray-900">{row.TotalOrders ?? 0}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Order list */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <h2 className="text-lg font-semibold text-gray-900 px-4 py-3 border-b border-gray-200">{ui('orders.todaysOrders.orderListHeading')}</h2>
            {listLoading && list.length === 0 ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.voucherNo')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.customer')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.mobile')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.client')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('common.status')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.emirate')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.area')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.deliveryDate')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.action')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {list.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                            {ui('orders.todaysOrders.noOrdersToday')}
                          </td>
                        </tr>
                      ) : (
                        list.map((row) => (
                          <tr key={row.Id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{row.VoucherNo || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{row.CustomerName || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.CustomerMobile || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{row.ClientName || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.Status || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.Emirate || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.Area || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(row.DeliveryDate)}</td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <button
                                onClick={() => navigate(`/home/orders/${row.Id}`)}
                                className="inline-flex items-center gap-1 text-primary hover:text-green-700 text-sm font-medium"
                              >
                                <Eye className="w-4 h-4" />
                                {ui('common.view')}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {total > 0 && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm text-gray-600">
                      {ui('orders.todaysOrders.showingToOf', { from, to, total })}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || listLoading}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {ui('common.previous')}
                      </button>
                      <span className="px-3 py-1.5 text-sm text-gray-600">
                        {ui('orders.todaysOrders.pageOf', { page, totalPages })}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages || listLoading}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                      >
                        {ui('common.next')}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TodaysOrders;
